<template>
  <div class="progress-circle">
    <!-- Ребристый круг с прогрессом -->
    <svg
      class="progress-circle__svg"
      width="300"
      height="300"
      viewBox="0 0 300 300"
    >
      <!-- Задний фон с ребрами -->
      <circle
        class="progress-circle__background"
        cx="150"
        cy="150"
        r="60"
        fill="none"
        stroke="rgba(255, 255, 255, 0.2)"
        stroke-width="4"
        stroke-dasharray="6 2"
      />

      <g class="progress-circle__rotate-wrapper">
        <circle
          class="progress-circle__background--dop-1"
          cx="150"
          cy="150"
          r="90"
          fill="none"
          stroke="rgba(255, 255, 255, 0.2)"
          stroke-width="4"
          stroke-dasharray="6 2"
        />
      </g>

      <g class="progress-circle__rotate-wrapper--dop-2">
        <circle
          class="progress-circle__background--dop-2"
          cx="150"
          cy="150"
          r="140"
          fill="none"
          stroke="rgba(255, 255, 255, 0.2)"
          stroke-width="4"
          stroke-dasharray="6 2"
        />
      </g>
      <!-- Прогресс-бар -->
      <g class="progress-circle__rotate-wrapper--dop-3">
        <!-- Определения для маски -->
        <defs>
          <mask id="progress-mask">
            <!-- Белый фон = видимая область -->
            <rect width="300" height="300" fill="black"/>
            <!-- Белый круг с анимацией = область, которая будет видна -->
            <circle
              cx="150"
              cy="150"
              r="60"
              fill="none"
              stroke="white"
              stroke-width="3"
              :stroke-dasharray="dashArray"
              :stroke-dashoffset="strokeDashoffset"
              transform="rotate(30 150 150)"
              style="transition: stroke-dashoffset 1s cubic-bezier(0.4, 0, 0.2, 1);"
            />
          </mask>
        </defs>
        
        <!-- Полный пунктирный круг, замаскированный по прогрессу -->
        <circle
          class="progress-circle__progress progress-circle__progress--dop-3"
          cx="150"
          cy="150"
          r="60"
          fill="none"
          stroke="#FFFFFF"
          stroke-width="3"
          stroke-dasharray="6 2"
          mask="url(#progress-mask)"
          transform="rotate(-120 150 150)"
        />
      </g>
    </svg>
    
    <!-- Контент в центре круга -->
    <div class="progress-circle__content">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'

interface Props {
  progress: number // от 0 до 100
}

const props = defineProps<Props>()

// Радиус и длина окружности
const radius = 60
const circumference = 2 * Math.PI * radius

// Массив для создания правильной анимации заполнения
const dashArray = computed(() => {
  return `${circumference} ${circumference}`
})

// Расчет offset для прогресса  
const strokeDashoffset = computed(() => {
  const progress = Math.min(Math.max(props.progress, 0), 100)
  return circumference - (progress / 100) * circumference
})

// Отладка
watch(() => props.progress, (newProgress, oldProgress) => {
  console.log('🔵 Progress changed:', oldProgress, '->', newProgress, 'Offset:', strokeDashoffset.value)
}, { immediate: true })
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';

.progress-circle {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  
  &__svg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  
  &__background {
    opacity: 0.3;
    &--dop-1 {
      opacity: 0.3;
      animation: scale-circle 10s ease infinite;
      animation-delay: 1.5s;
      transform-origin: 150px 150px;
    }

    &--dop-2 {
      opacity: 0.3;
      animation: scale-circle 10s ease infinite;
      // animation-delay: 1.5s;
      transform-origin: 150px 150px;
    }

    &--dop-3 {
      opacity: 0.3;
    }
  }

  &__rotate-wrapper {
    animation: rotate-circle 30s linear infinite;
    transform-origin: 150px 150px;

    &--dop-2 {
      animation: rotate-circle 30s linear infinite;
      animation-delay: 1.5s;
      transform-origin: 150px 150px;
    }

    &--dop-3 {
      /* Группа для прогресс-бара без анимаций */
      pointer-events: none;
    }
  }
  
  &__progress {
    z-index: 1;
    transition: stroke-dashoffset 2s cubic-bezier(0.4, 0, 0.2, 1) !important;
    filter: drop-shadow(0 0 12px rgba(255, 255, 255, 0.6));

    &--dop-3 {
      /* Принудительно применяем transition */
      transition: stroke-dashoffset 1s cubic-bezier(0.4, 0, 0.2, 1) !important;
    }
  }
  
  &__ridges {
    pointer-events: none;
  }
  
  &__content {
    position: relative;
  }
}

@keyframes rotate-circle {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
}

@keyframes scale-circle {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
}
</style>